import type { QueryClient } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import type { QuoteDetailData } from '@/lib/api/quote-details'

const CONCURRENT_DELETE_MESSAGE = 'More than one concurrent Delete requests detected'

/**
 * A DELETE that failed because the row is already gone or going: 404, or the
 * Dataverse 400 a second DELETE gets while the first is still committing.
 */
export function isAlreadyDeletedError(error: unknown) {
  if (!isAxiosError(error) || error.config?.method !== 'delete') return false
  if (error.response?.status === 404) return true
  const message = (error.response?.data as { error?: { message?: unknown } } | undefined)?.error?.message
  return error.response?.status === 400 && typeof message === 'string' && message.includes(CONCURRENT_DELETE_MESSAGE)
}

/** Rows held by every quote-line delete still in flight. */
export function pendingDeletedRows(client: QueryClient): QuoteDetailData[] {
  return client
    .getMutationCache()
    .findAll({ mutationKey: ['delete', 'quotedetails'], status: 'pending' })
    .flatMap((mutation) => (mutation.state.variables as QuoteDetailData[] | undefined) ?? [])
}

/**
 * `rows` without what deleting `deleted` removes: the rows themselves, plus the
 * accessories and package members the cascade plugin takes with them (it Ors
 * both keys). Current quote only: global ids are shared across quote versions.
 */
export function withoutDeletedRows(rows: QuoteDetailData[], deleted: QuoteDetailData[]) {
  const deletedIds = new Set(deleted.map((qd) => qd.quotedetailid))
  const deletedGlobalIds = new Set(
    deleted.map((qd) => qd.dsy_cpqfields.dsy_globalid).filter((id): id is string => !!id)
  )
  return rows.filter((d) => {
    const parentGlobalId = d.dsy_cpqfields.dsy_parentglobalid
    const packageGlobalId = d.dsy_cpqfields.dsy_packageglobalid
    return (
      !deletedIds.has(d.quotedetailid) &&
      !(parentGlobalId && deletedGlobalIds.has(parentGlobalId)) &&
      !(packageGlobalId && deletedGlobalIds.has(packageGlobalId))
    )
  })
}
